import type { CSSProperties } from 'react'
import type { GradingProvider } from '../lib/types'
import { ReactComponent as CgcLogoSvg } from '../assets/brand/cgc.svg'
import { ReactComponent as EvoLogoFullSvgAsset } from '../assets/brand/logo-full.svg'
import { ReactComponent as PsaLogoSvg } from '../assets/brand/psa.svg'
import { ReactComponent as TagLogoSvg } from '../assets/brand/tag.svg'

/* Evo logo mark — inherits currentColor */
export function EvoLogo({ height = 18 }: { height?: number }) {
  return (
    <span aria-hidden style={maskStyle('/logo.svg', (height * 1601) / 2000, height)} />
  )
}

/* Full "evo" wordmark — CSS mask; `fill` can be a gradient (slab header) */
export function EvoLogoFull({
  height = 32,
  fill,
}: {
  height?: number
  fill?: string
}) {
  return (
    <span
      aria-hidden
      style={maskStyle('/logo-full.svg', (height * 6400) / 2000, height, fill)}
    />
  )
}

export function EvoLogoFullSvg({ height = 32 }: { height?: number }) {
  return (
    <EvoLogoFullSvgAsset
      aria-hidden
      style={{
        display: 'block',
        width: (height * 6400) / 2000,
        height,
        color: 'currentColor',
      }}
    />
  )
}

export function EvoLogoFullGradientSvg({ height = 32 }: { height?: number }) {
  const gradientId = `evo-logo-full-gradient-${height}`
  const maskId = `evo-logo-full-mask-${height}`

  return (
    <svg
      aria-hidden
      width={(height * 6400) / 2000}
      height={height}
      viewBox="0 0 6400 2000"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="3200" y1="0" x2="3200" y2="2000" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#999999" />
        </linearGradient>
        <mask id={maskId} x="0" y="0" width="6400" height="2000" maskUnits="userSpaceOnUse">
          <EvoLogoFullSvgAsset
            width={6400}
            height={2000}
            style={{ color: '#fff', display: 'block' }}
          />
        </mask>
      </defs>
      <rect width="6400" height="2000" fill={`url(#${gradientId})`} mask={`url(#${maskId})`} />
    </svg>
  )
}

const PROVIDER_RATIOS: Record<GradingProvider, number> = {
  PSA: 64 / 25,
  TAG: 64 / 24,
  CGC: 59 / 18,
}

const PROVIDER_NATIVE_SIZES: Record<GradingProvider, { width: number; height: number }> = {
  PSA: { width: 64, height: 25 },
  TAG: { width: 64, height: 24 },
  CGC: { width: 59, height: 18 },
}

/* Grading provider wordmarks (PSA / TAG / CGC) — CSS mask; `fill` can be a
   gradient (slab header) or any color, defaults to currentColor */
export function ProviderLogo({
  provider,
  height = 14,
  fill,
}: {
  provider: GradingProvider
  height?: number
  fill?: string
}) {
  return (
    <span
      role="img"
      aria-label={provider}
      style={maskStyle(
        `/providers/${provider.toLowerCase()}.svg`,
        height * PROVIDER_RATIOS[provider],
        height,
        fill,
      )}
    />
  )
}

const PROVIDER_SVG_COMPONENTS = {
  PSA: PsaLogoSvg,
  TAG: TagLogoSvg,
  CGC: CgcLogoSvg,
}

export function ProviderLogoSvg({
  provider,
  height = 14,
  gradient = false,
}: {
  provider: GradingProvider
  height?: number
  gradient?: boolean
}) {
  const Logo = PROVIDER_SVG_COMPONENTS[provider]

  if (gradient) {
    const width = height * PROVIDER_RATIOS[provider]
    const nativeSize = PROVIDER_NATIVE_SIZES[provider]
    const gradientId = `${provider.toLowerCase()}-logo-gradient-${height}`
    const maskId = `${provider.toLowerCase()}-logo-mask-${height}`

    return (
      <svg
        role="img"
        aria-label={provider}
        width={width}
        height={height}
        viewBox={`0 0 ${nativeSize.width} ${nativeSize.height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1={nativeSize.width / 2}
            y1="0"
            x2={nativeSize.width / 2}
            y2={nativeSize.height}
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFFFFF" />
            <stop offset="1" stopColor="#999999" />
          </linearGradient>
          <mask
            id={maskId}
            x="0"
            y="0"
            width={nativeSize.width}
            height={nativeSize.height}
            maskUnits="userSpaceOnUse"
          >
            <Logo
              width={nativeSize.width}
              height={nativeSize.height}
              style={{ color: '#fff', display: 'block' }}
            />
          </mask>
        </defs>
        <rect
          width={nativeSize.width}
          height={nativeSize.height}
          fill={`url(#${gradientId})`}
          mask={`url(#${maskId})`}
        />
      </svg>
    )
  }

  return (
    <Logo
      role="img"
      aria-label={provider}
      style={{
        display: 'block',
        width: height * PROVIDER_RATIOS[provider],
        height,
        color: 'currentColor',
      }}
    />
  )
}

/* Flip icon from assets/icons/flip.svg — md tier: 18px / 3px stroke */
export function FlipIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 8H15C18.3137 8 21 10.6863 21 14C21 17.3137 18.3137 20 15 20H11"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 4L5.8462 4.87652C3.94873 6.31801 3 7.03875 3 8C3 8.96125 3.94873 9.68199 5.8462 11.1235L7 12"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* Discord glyph — exact path from the website footer (18px box) */
export function DiscordIcon({ size = 18 }: { size?: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
      }}
    >
      <svg width={size} height={(size * 13.9338) / 18} viewBox="0 0 18 13.9338" fill="none" aria-hidden>
        <path
          d="M15.2477 1.16695C14.0825 0.621794 12.8367 0.225598 11.5342 0C11.3742 0.289208 11.1873 0.678199 11.0585 0.98764C9.67387 0.779413 8.30201 0.779413 6.94287 0.98764C6.81404 0.678199 6.62292 0.289208 6.46152 0C5.1576 0.225598 3.91032 0.623249 2.74514 1.16984C0.394982 4.72124 -0.242108 8.18443 0.0764375 11.5984C1.63519 12.7625 3.14581 13.4696 4.63093 13.9323C4.99762 13.4277 5.32465 12.8912 5.60638 12.3258C5.06981 12.1219 4.5559 11.8703 4.07031 11.5782C4.19913 11.4828 4.32514 11.383 4.44689 11.2803C7.40865 12.6656 10.6267 12.6656 13.5531 11.2803C13.6762 11.383 13.8022 11.4828 13.9296 11.5782C13.4426 11.8717 12.9273 12.1233 12.3907 12.3273C12.6724 12.8912 12.9981 13.4291 13.3662 13.9338C14.8527 13.471 16.3647 12.7639 17.9235 11.5984C18.2973 7.64073 17.285 4.20934 15.2477 1.16695ZM6.00988 9.49886C5.12079 9.49886 4.39166 8.66884 4.39166 7.65808C4.39166 6.64732 5.10522 5.81587 6.00988 5.81587C6.91457 5.81587 7.64367 6.64586 7.6281 7.65808C7.62951 8.66884 6.91457 9.49886 6.00988 9.49886ZM11.9901 9.49886C11.101 9.49886 10.3718 8.66884 10.3718 7.65808C10.3718 6.64732 11.0854 5.81587 11.9901 5.81587C12.8947 5.81587 13.6238 6.64586 13.6083 7.65808C13.6083 8.66884 12.8947 9.49886 11.9901 9.49886Z"
          fill="currentColor"
        />
      </svg>
    </span>
  )
}

/* Mail glyph — exact paths from the website footer (mail-01, 18px) */
export function MailIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M1.5 4.5L6.68477 7.43773C8.5962 8.52075 9.4038 8.52075 11.3152 7.43773L16.5 4.5"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M1.51183 10.1067C1.56086 12.4059 1.58537 13.5554 2.43372 14.4071C3.28206 15.2586 4.46275 15.2882 6.82412 15.3476C8.27948 15.3842 9.72053 15.3842 11.1759 15.3476C13.5373 15.2882 14.7179 15.2586 15.5663 14.4071C16.4147 13.5554 16.4392 12.4059 16.4881 10.1067C16.504 9.36743 16.504 8.63258 16.4881 7.8933C16.4392 5.59415 16.4147 4.44457 15.5663 3.593C14.7179 2.74142 13.5373 2.71176 11.1759 2.65243C9.72053 2.61586 8.27947 2.61586 6.82411 2.65242C4.46275 2.71175 3.28206 2.74141 2.43371 3.59299C1.58537 4.44456 1.56085 5.59414 1.51182 7.8933C1.49605 8.63258 1.49606 9.36743 1.51183 10.1067Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

/* Apple logo — official Evo asset (apple.svg), inherits currentColor */
export function AppleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M20.9144 8.1816C20.7752 8.2896 18.3176 9.6744 18.3176 12.7536C18.3176 16.3152 21.4448 17.5752 21.5384 17.6064C21.524 17.6832 21.0416 19.332 19.8896 21.012C18.8624 22.4904 17.7896 23.9664 16.1576 23.9664C14.5256 23.9664 14.1056 23.0184 12.2216 23.0184C10.3856 23.0184 9.7328 23.9976 8.24 23.9976C6.7472 23.9976 5.7056 22.6296 4.508 20.9496C3.1208 18.9768 2 15.912 2 13.0032C2 8.3376 5.0336 5.8632 8.0192 5.8632C9.6056 5.8632 10.928 6.9048 11.924 6.9048C12.872 6.9048 14.3504 5.8008 16.1552 5.8008C16.8392 5.8008 19.2968 5.8632 20.9144 8.1816ZM15.2984 3.8256C16.0448 2.94 16.5728 1.7112 16.5728 0.4824C16.5728 0.312 16.5584 0.1392 16.5272 0C15.3128 0.0456 13.868 0.8088 12.9968 1.8192C12.3128 2.5968 11.6744 3.8256 11.6744 5.0712C11.6744 5.2584 11.7056 5.4456 11.72 5.5056C11.7968 5.52 11.9216 5.5368 12.0464 5.5368C13.136 5.5368 14.5064 4.8072 15.2984 3.8256Z"
        fill="currentColor"
      />
    </svg>
  )
}

/* Android logo — official Evo asset (android.svg), inherits currentColor */
export function AndroidIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path fillRule="evenodd" clipRule="evenodd" d="M20 10C20.5523 10 21 10.4477 21 11V17C21 17.5523 20.5523 18 20 18C19.4477 18 19 17.5523 19 17V11C19 10.4477 19.4477 10 20 10Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M15 18C15.5523 18 16 18.4477 16 19V22C16 22.5523 15.5523 23 15 23C14.4477 23 14 22.5523 14 22V19C14 18.4477 14.4477 18 15 18Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M9 18C9.55228 18 10 18.4477 10 19V22C10 22.5523 9.55228 23 9 23C8.44772 23 8 22.5523 8 22V19C8 18.4477 8.44772 18 9 18Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M4 10C4.55228 10 5 10.4477 5 11V17C5 17.5523 4.55228 18 4 18C3.44772 18 3 17.5523 3 17V11C3 10.4477 3.44772 10 4 10Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M7.90006 1.20006C8.34189 0.868688 8.96869 0.958231 9.30006 1.40006L10.8001 3.40006C11.1315 3.84189 11.0419 4.46869 10.6001 4.80006C10.1583 5.13143 9.53143 5.04189 9.20006 4.60006L7.70006 2.60006C7.36869 2.15823 7.45823 1.53143 7.90006 1.20006ZM16.1001 1.20006C16.5419 1.53143 16.6315 2.15823 16.3001 2.60006L14.8001 4.60006C14.4687 5.04189 13.8419 5.13143 13.4001 4.80006C12.9583 4.46869 12.8687 3.84189 13.2001 3.40006L14.7001 1.40006C15.0315 0.958231 15.6583 0.868688 16.1001 1.20006Z" fill="currentColor" />
      <path d="M6.7956 9.25C6.29466 9.25 6.04419 9.25 5.89412 9.06543C5.74405 8.88087 5.79006 8.66007 5.88208 8.21847C6.4733 5.38139 8.98803 3.25 12.0006 3.25C15.0131 3.25 17.5279 5.38139 18.1191 8.21847C18.2111 8.66007 18.2571 8.88087 18.107 9.06543C17.957 9.25 17.7065 9.25 17.2056 9.25H6.7956Z" fill="currentColor" />
      <path d="M6.75001 10.75C6.2786 10.75 6.0429 10.75 5.89645 10.8964C5.75001 11.0429 5.75001 11.2786 5.75001 11.75L5.75 16.0494C5.74997 16.7142 5.74994 17.2871 5.81161 17.7458C5.87771 18.2375 6.02677 18.7087 6.40902 19.091C6.79126 19.4732 7.26253 19.6223 7.75416 19.6884C8.21289 19.7501 8.78573 19.75 9.45057 19.75H14.5494C15.2142 19.75 15.7871 19.7501 16.2459 19.6884C16.7375 19.6223 17.2088 19.4732 17.591 19.091C17.9732 18.7087 18.1223 18.2375 18.1884 17.7458C18.2501 17.2871 18.25 16.7143 18.25 16.0494V11.75C18.25 11.2786 18.25 11.0429 18.1036 10.8964C17.9571 10.75 17.7214 10.75 17.25 10.75H6.75001Z" fill="currentColor" />
    </svg>
  )
}

/* Copy — official Evo asset (copy.svg, Hugeicons stroke 3), currentColor */
export function CopyIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 15C9 12.1716 9 10.7574 9.87868 9.87868C10.7574 9 12.1716 9 15 9H16C18.8284 9 20.2426 9 21.1213 9.87868C22 10.7574 22 12.1716 22 15V16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H15C12.1716 22 10.7574 22 9.87868 21.1213C9 20.2426 9 18.8284 9 16V15Z"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.9999 9C16.9975 6.04291 16.9528 4.51121 16.092 3.46243C15.9258 3.25989 15.7401 3.07418 15.5376 2.90796C14.4312 2 12.7875 2 9.5 2C6.21252 2 4.56878 2 3.46243 2.90796C3.25989 3.07417 3.07418 3.25989 2.90796 3.46243C2 4.56878 2 6.21252 2 9.5C2 12.7875 2 14.4312 2.90796 15.5376C3.07417 15.7401 3.25989 15.9258 3.46243 16.092C4.51121 16.9528 6.04291 16.9975 9 16.9999"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* Close — the app's sheet close button (cancel-bg): faint circle + tertiary X */
export function CloseIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25Z"
        fill="var(--text-faint)"
      />
      <path
        d="M15 9L9.00041 14.9996M14.9996 15L9 9.00043"
        stroke="var(--text-secondary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function maskStyle(
  url: string,
  width: number,
  height: number,
  fill?: string,
): CSSProperties {
  const mask = `url(${url}) no-repeat center / contain`
  return {
    display: 'inline-block',
    width,
    height,
    // A gradient fill paints the wordmark with the slab's white→grey ramp;
    // otherwise inherit currentColor.
    ...(fill && fill.includes('gradient')
      ? { backgroundImage: fill }
      : { backgroundColor: fill ?? 'currentColor' }),
    WebkitMask: mask,
    mask,
  }
}
